import React, { useState } from 'react';
import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { ChevronLeft20Regular, ChevronRight20Regular, ChevronDown20Regular, ChevronLeft16Filled, ChevronRight16Filled } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  todayButton: {
    minWidth: '60px',
  },
  navButtons: {
    display: 'flex',
    gap: '4px',
  },
  dateRange: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
  currentDate: {
    marginLeft: 'auto',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  weekGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    flex: 1,
    minHeight: 0,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    position: 'relative',
  },
  dayColumn: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    position: 'relative',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  dayHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.padding('12px', '8px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  dayNumber: {
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '1',
  },
  dayName: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    marginTop: '4px',
  },
  today: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1),
  },
  dayContent: {
    flex: 1,
    ...shorthands.padding('8px'),
    position: 'relative',
  },
  eventsContainer: {
    position: 'absolute',
    top: '60px', // Below day headers
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  event: {
    position: 'absolute',
    ...shorthands.padding('6px', '12px'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.borderLeft('4px', 'solid', 'transparent'), // Will be set via inline style
    fontSize: '12px',
    cursor: 'pointer',
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '32px',
    ...shorthands.overflow('hidden'),
    transition: 'filter 0.2s ease',
    '&:hover': {
      filter: 'brightness(95%)',
    },
  },
  eventContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    ...shorthands.overflow('hidden'),
  },
  eventTitle: {
    fontWeight: 500,
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  eventDetails: {
    fontSize: '11px',
    opacity: 0.9,
  },
  eventArrow: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  allDayLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('4px', '8px'),
  },
});

interface CalendarEvent {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  color: string;
  priority?: string;
}

interface BookingCalendarProps {
  events?: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onEventDoubleClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
}

// Utility function to lighten a hex color
const lightenColor = (hex: string, factor: number = 1.1): string => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Apply brightness factor
  const newR = Math.min(255, Math.round(r * factor));
  const newG = Math.min(255, Math.round(g * factor));
  const newB = Math.min(255, Math.round(b * factor));

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

export default function BookingCalendar({ events = [], onEventClick, onEventDoubleClick, onDateClick }: BookingCalendarProps) {
  const styles = useStyles();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day; // Adjust to Sunday
    return new Date(today.setDate(diff));
  });

  const getWeekDays = (startDate: Date): Date[] => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    const sunday = new Date(today.setDate(diff));
    setCurrentWeekStart(sunday);
  };

  const formatDateRange = (startDate: Date): string => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
    const year = startDate.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDate.getDate()}-${endDate.getDate()}, ${year}`;
    } else {
      return `${startMonth} ${startDate.getDate()} - ${endMonth} ${endDate.getDate()}, ${year}`;
    }
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const calculateEventPosition = (event: CalendarEvent, weekStart: Date) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    const eventStart = new Date(event.startDate);
    const eventEnd = new Date(event.endDate);

    eventStart.setHours(0, 0, 0, 0);
    eventEnd.setHours(23, 59, 59, 999);

    // Check if event is in this week
    if (eventEnd < weekStart || eventStart > weekEnd) {
      return null;
    }

    // Calculate start and end positions (0-6 for days of week)
    const clampedStart = eventStart < weekStart ? weekStart : eventStart;
    const clampedEnd = eventEnd > weekEnd ? weekEnd : eventEnd;

    const startDayIndex = Math.floor((clampedStart.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000));
    const endDayIndex = Math.floor((clampedEnd.getTime() - weekStart.getTime()) / (24 * 60 * 60 * 1000));

    // Calculate percentage positions
    const left = (startDayIndex / 7) * 100;
    const width = ((endDayIndex - startDayIndex + 1) / 7) * 100;

    // Check if event extends beyond visible range
    const extendsLeft = eventStart < weekStart;
    const extendsRight = eventEnd > weekEnd;

    return {
      left,
      width,
      extendsLeft,
      extendsRight,
    };
  };

  const getVisibleEvents = () => {
    return events
      .map(event => {
        const position = calculateEventPosition(event, currentWeekStart);
        if (!position) return null;
        return { event, position };
      })
      .filter(item => item !== null) as Array<{
        event: CalendarEvent;
        position: { left: number; width: number; extendsLeft: boolean; extendsRight: boolean };
      }>;
  };

  const weekDays = getWeekDays(currentWeekStart);
  const today = new Date();
  const currentDateString = today.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          className={styles.todayButton}
          onClick={goToToday}
        >
          Today
        </Button>

        <div className={styles.navButtons}>
          <Button
            appearance="subtle"
            icon={<ChevronLeft20Regular />}
            onClick={() => navigateWeek('prev')}
          />
          <Button
            appearance="subtle"
            icon={<ChevronRight20Regular />}
            onClick={() => navigateWeek('next')}
          />
        </div>

        <div className={styles.dateRange}>
          {formatDateRange(currentWeekStart)}
          <ChevronDown20Regular />
        </div>

        <div className={styles.currentDate}>
          {currentDateString}
        </div>
      </div>

      {/* Week Grid */}
      <div className={styles.weekGrid}>
        {weekDays.map((date, index) => {
          const isTodayDate = isToday(date);

          return (
            <div key={index} className={styles.dayColumn}>
              <div className={`${styles.dayHeader} ${isTodayDate ? styles.today : ''}`}>
                <div className={styles.dayNumber}>{date.getDate()}</div>
                <div className={styles.dayName}>
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
              </div>
              <div
                className={styles.dayContent}
                onClick={() => onDateClick?.(date)}
              />
            </div>
          );
        })}

        {/* Events Layer */}
        <div className={styles.eventsContainer}>
          {getVisibleEvents().map(({ event, position }, index) => {
            const lighterBackground = lightenColor(event.color, 1.1);

            return (
              <div
                key={event.id}
                className={styles.event}
                style={{
                  backgroundColor: lighterBackground,
                  borderLeftColor: event.color,
                  left: `${position.left}%`,
                  width: `${position.width}%`,
                  top: `${8 + (index * 40)}px`, // Stack events vertically
                }}
                onClick={() => onEventClick?.(event)}
                onDoubleClick={() => onEventDoubleClick?.(event)}
              >
                <div className={styles.eventContent}>
                  {position.extendsLeft && (
                    <div className={styles.eventArrow}>
                      <ChevronLeft16Filled />
                    </div>
                  )}
                  <div className={styles.eventTitle}>{event.title}</div>
                </div>
                {position.extendsRight && (
                  <div className={styles.eventArrow}>
                    <ChevronRight16Filled />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
